import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DEFAULT_PERSONAS } from "./Persona";
import { getTokenOrRefresh, postURL, realtimeFeedback } from "../utils";
import ChatStatus from "./ChatStatus";
import ChatInput from "./ChatInput";
// import '../assets/styles/ChatDetails.css';
import ChatOutput from "./ChatOutput";
import WaveformCanvas from "./WaveformCanvas";
import ChatHistory from "./ChatHistory";
import { MessageResponse } from "../models/Simulation";
import { Dialog } from "@mui/material";
import { SummaryBox } from "./SummaryBox";
import { getMessageLimit, getExcuseMessage } from "../utils";
import { mapJsonToSimResponse } from "../models/Simulation";
import "../assets/styles/FullscreenMode.css";
import { endChat, getDataFromLocalStorage, setDataInLocalStorage, fetchAuthenticatedUrl, checkIfRandomNumberIsOne } from "../utils";
import { useDialog } from "../pages/hooks/useDialog";
import { SimulationFeedbackDialog } from "../pages/SimulationFeedback/SimulationFeedbackDialog";
import ChatOutputWithImage from "./ChatOutputWithImage";

// Global cache for emotion images to avoid refetching
const emotionImagesCache = new Map();

// Cache utility functions
export const clearEmotionImagesCache = () => {
    emotionImagesCache.clear();
    console.log('[EMOTION_DEBUG] 🗑️ Cleared emotion images cache');
};

export const getCacheStats = () => {
    return {
        size: emotionImagesCache.size,
        keys: Array.from(emotionImagesCache.keys())
    };
};

const CHAT_OUTPUT = "o";
const CHAT_INPUT = "i";

function ChatDetails({
  simulation,
  chat,
  personaId,
  refreshCheckList,
  showFeedback,
  setFeedbackActive,
  summary,
  startTimer,
  stopTimer,
  resetTimer,
  currentResponseTime
}) {
  const location = useLocation();
  const [persona, setPersona] = useState();
  const [error, setError] = useState(false);
  const [azureToken, setAzureToken] = useState(false);
  const [messages, setMessages] = useState(chat.messages);
  const [nextStage, setNextStage] = useState(CHAT_INPUT);
  const [userMessage, setUserMessage] = useState("");
  const [botMessage, setBotMessage] = useState("");
  const historyContainerRef = useRef(null); //used for auto-scroll
  const simId = localStorage.getItem("sim_id");
  const chatId = localStorage.getItem("chat_id");
  const [personaImage, setPersonaImage] = useState()
  const [audioBlob, setAudioBlob] = useState(null);
  const [feedbackMessages, setFeedbackMessages] = useState({});
  const [feedbackConfig, setFeedbackConfig] = useState(simulation.config.realtimeFeedbackConfig);
  const [showSummaryBox, setShowSummaryBox] = useState(false);
  const [showHalo, setShowHalo] = useState(false);
  const [messageLimitReached, setMessageLimitReached] = useState(false);
  const navigate = useNavigate();
  const [isChatInputMounted, setIsChatInputMounted] = useState(true);
  const [stage, setStage] = useState(simulation.chatResponse[0].stage);
  const [speakingStyle, setSpeakingStyle] = useState("default");
  const [speakingStyleCounter, setSpeakingStyleCounter] = useState(0);
  const [emotionProcessingCounter, setEmotionProcessingCounter] = useState(0);
  const [lastValidEmotion, setLastValidEmotion] = useState("Neutral");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  // Removed recordingMethods state - now using chatInputRef directly
  const [mediaStream, setMediaStream] = useState(null);
  const [persistentMediaStream, setPersistentMediaStream] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRecognizerReady, setIsRecognizerReady] = useState(false);
  
  // Waveform reusable component ref
  const waveformRef = useRef(null);
  
  // Fullscreen waveform ref to avoid conflicts with ChatInput
  const fullscreenWaveformRef = useRef(null);
  
  // ChatInput component ref for direct method access
  const chatInputRef = useRef(null);

  // Memoized waveform props to prevent unnecessary re-renders
  const waveformProps = useMemo(() => ({
    width: 300,
    height: 100,
    isBotSpeaking: nextStage === CHAT_OUTPUT
  }), [nextStage]);

  const fullscreenWaveformProps = useMemo(() => ({
    width: 300,
    height: 80,
    isBotSpeaking: nextStage === CHAT_OUTPUT
  }), [nextStage]);

  // Single WaveformCanvas component that handles all cases
  const WaveformComponent = useMemo(() => {
    if (nextStage === CHAT_INPUT && isChatInputMounted) {
      return null; // ChatInput handles its own waveform
    }
    
    if (isFullscreen) {
      if (nextStage === CHAT_OUTPUT) {
        return (
          <div className="inline-waveform-container">
            <WaveformCanvas 
              ref={fullscreenWaveformRef} 
              {...fullscreenWaveformProps}
              isBotSpeaking={true}
            />
          </div>
        );
      } else if (!isProcessing || isRecording) {
        // Show waveform when not processing OR when recording
        return (
          <div className="inline-waveform-container">
            <WaveformCanvas 
              ref={fullscreenWaveformRef} 
              {...fullscreenWaveformProps}
              isBotSpeaking={false}
            />
          </div>
        );
      }
    } else {
      return (
        <div>
          <WaveformCanvas 
            ref={waveformRef} 
            {...waveformProps}
          />
        </div>
      );
    }
    
    return null;
  }, [nextStage, isChatInputMounted, isFullscreen, isProcessing, isRecording, waveformProps, fullscreenWaveformProps]);
  
  // Emotion images state
  const [emotionImages, setEmotionImages] = useState({});
  const [isLoadingImages, setIsLoadingImages] = useState(true);
  const [currentImageIndices, setCurrentImageIndices] = useState({});
  const [emotionImageCounts, setEmotionImageCounts] = useState({});
  const [displayedImagePath, setDisplayedImagePath] = useState(null);

  // Web Audio waveform setup function via reusable component
  const setupWaveformFromSpeaker = useCallback((speakerDestination) => {
    // Use fullscreen waveform ref when in fullscreen mode, otherwise use regular ref
    const targetRef = isFullscreen ? fullscreenWaveformRef : waveformRef;
    console.log('setupWaveformFromSpeaker called:', { isFullscreen, targetRef: targetRef.current, speakerDestination });
    if (targetRef.current && targetRef.current.setupFromSpeakerDestination) {
      console.log('Setting up waveform from speaker destination');
      targetRef.current.setupFromSpeakerDestination(speakerDestination);
    } else {
      console.warn('Target waveform ref not available:', { targetRef: targetRef.current, isFullscreen });
    }
  }, [isFullscreen]);

  // SimulationFeedback dialog
  const simulationFeedbackDialog = useDialog({
    component: SimulationFeedbackDialog,
    initialState: {
      open: false,
    },
  });


  const chatInputURL = `${process.env.REACT_APP_SIM_BACKEND_HOST}/sim/${simulation.simulationId || simId
    }/${chat.chatId || chatId}/msg`;

  // Function to validate emotion and return fallback if needed
  const validateEmotion = (receivedEmotion) => {
    // First try exact match
    let emotionExists = emotionImages[receivedEmotion] && emotionImages[receivedEmotion].length > 0;
    let matchedEmotion = receivedEmotion;
    
    // If no exact match, try case-insensitive match
    if (!emotionExists) {
      const availableEmotions = Object.keys(emotionImages);
      const caseInsensitiveMatch = availableEmotions.find(emotion => 
        emotion.toLowerCase() === receivedEmotion.toLowerCase()
      );
      
      if (caseInsensitiveMatch) {
        emotionExists = emotionImages[caseInsensitiveMatch] && emotionImages[caseInsensitiveMatch].length > 0;
        matchedEmotion = caseInsensitiveMatch;
        console.log('[EMOTION_DEBUG] 🔄 Case-insensitive match found:', receivedEmotion, '->', caseInsensitiveMatch);
      }
    }
    
    if (emotionExists) {
      console.log('[EMOTION_DEBUG] ✅ Valid emotion received:', receivedEmotion, '->', matchedEmotion, `(${emotionImages[matchedEmotion].length} images available)`);
      setLastValidEmotion(matchedEmotion);
      return matchedEmotion;
    } else {
      console.log('[EMOTION_DEBUG] ❌ Invalid emotion received:', receivedEmotion, '- falling back to last valid emotion:', lastValidEmotion);
      console.log('[EMOTION_DEBUG] 📋 Available emotions:', Object.keys(emotionImages).filter(emotion => emotionImages[emotion] && emotionImages[emotion].length > 0));
      return lastValidEmotion;
    }
  };

  const fetchEmotionImages = async (simType) => {
    console.log('[EMOTION_DEBUG] 🌐 Fetching emotion images for simType:', simType);
    
    // Check cache first
    if (emotionImagesCache.has(simType)) {
      console.log('[EMOTION_DEBUG] 💾 Using cached emotion images for simType:', simType);
      const cachedData = emotionImagesCache.get(simType);
      setEmotionImages(cachedData);
      
      // Initialize image counts for all emotions
      const imageCounts = {};
      Object.keys(cachedData).forEach(emotion => {
        const imageCount = cachedData[emotion]?.length || 0;
        imageCounts[emotion] = imageCount;
        console.log(`[EMOTION_DEBUG] 🎭 Cached emotion: ${emotion} (${imageCount} images) - FROM CACHE`);
      });
      setEmotionImageCounts(imageCounts);
      setIsLoadingImages(false);
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_SIM_BACKEND_HOST}/sim/emotion-folders/${simType}`);
      if (response.ok) {
        const result = await response.json();
        console.log('[EMOTION_DEBUG] 📥 Fetched emotion images result:', result);
        
        if (result.success) {
          console.log('[EMOTION_DEBUG] ✅ Successfully fetched emotion data:', result.data);
          
          // Cache the fetched data
          emotionImagesCache.set(simType, result.data);
          console.log('[EMOTION_DEBUG] 💾 Cached emotion images for simType:', simType);
          
          setEmotionImages(result.data);
          
          // Initialize image counts for all emotions
          const imageCounts = {};
          Object.keys(result.data).forEach(emotion => {
            const imageCount = result.data[emotion]?.length || 0;
            imageCounts[emotion] = imageCount;
            console.log(`[EMOTION_DEBUG] 🎭 Fetched emotion: ${emotion} (${imageCount} images) - FROM API`);
          });
          setEmotionImageCounts(imageCounts);
        } else {
          console.log('[EMOTION_DEBUG] ❌ Failed to fetch emotion images - result.success is false');
        }
      } else {
        console.log('[EMOTION_DEBUG] ❌ Failed to fetch emotion images - response not ok:', response.status);
      }
    } catch (error) {
      console.error('[EMOTION_DEBUG] ❌ Failed to fetch emotion images:', error);
    } finally {
      setIsLoadingImages(false);
    }
  };

  const appendMessage = (sender, content, responseTime = null) => {
    const newMessage = new MessageResponse(sender, content, new Date(), responseTime);
    setMessages((prevMessages) => [...prevMessages, newMessage]);
  };
  useEffect(() => {
    const feedbackMesages = getDataFromLocalStorage(`feedbackMessages-${simulation.simulationId}-${chat.chatId}`);
    if (feedbackMesages) {
      setFeedbackMessages(JSON.parse(feedbackMesages));
    }
  }, []);

  useEffect(() => {
    if (messages.length >= getMessageLimit()) {
      setMessageLimitReached(true);
      // appendMessage("B","Sorry to cut this short, but I have to go now.");
      setNextStage(CHAT_OUTPUT);
      setBotMessage(getExcuseMessage());
      handleEndChat(false);
    }
  }, [messages]);

  useEffect(() => {
    const fetchRealTimeFeedback = async () => {
      console.log("fetching real time feedback", messages, audioBlob, simulation);
      if (feedbackConfig.length === 0) {
        return;
      }
      const resp = await realtimeFeedback(`${process.env.REACT_APP_REALTIME_FEEDBACK_BACKEND_HOST}/realtime-feedback`, messages, audioBlob, simulation.simType, feedbackConfig);
      setFeedbackMessages({ ...feedbackMessages, [messages.length - 1]: { feedback: resp.response } });
      if (Object.keys(resp.response).length > 0) {
        console.log("feedback config", feedbackConfig);
        feedbackConfig.forEach(item => {
          console.log("feedback config", item, resp.response[item]?.score);
          if (resp.response[item]?.score >= 1 && resp.response[item]?.suggestion1 !== 'None') {
            console.log("feedback active", item);
            setFeedbackActive(true);
          }
        })
      }
      setDataInLocalStorage(`feedbackMessages-${simulation.simulationId}-${chat.chatId}`, JSON.stringify({ ...feedbackMessages, [messages.length - 1]: { feedback: resp.response } }));
      console.log("real time feedback response", resp);
    }
    if (audioBlob && messages.length > 4 && checkIfRandomNumberIsOne()) {
      fetchRealTimeFeedback();
    }
  }, [simulation, audioBlob]);
  useEffect(() => {
    const fetchImgUrl = `${process.env.REACT_APP_SIM_BACKEND_HOST}/sim/${personaId}/image`;
    postURL(fetchImgUrl).then((resp) => {
      console.log(resp);
      setPersonaImage(resp.data);
    });
    console.log("persona image is ", personaImage);
  }, [personaId]);

  // Fetch emotion images when simType changes
  useEffect(() => {
    if (simulation.simType && simulation.simType !== 'default') {
      fetchEmotionImages(simulation.simType);
    } else {
      setIsLoadingImages(false);
    }
  }, [simulation.simType]);

  // Cycle through image index when emotion processing counter changes
  useEffect(() => {
    // Only log in development and reduce frequency
    if (process.env.NODE_ENV === 'development' && emotionProcessingCounter % 5 === 0) {
      console.log('[EMOTION_DEBUG] 🎯 Emotion processing counter changed:', emotionProcessingCounter, 'speakingStyle:', speakingStyle);
    }
    
    // Only proceed if we have emotion images loaded and the current speaking style exists
    if (emotionImages && emotionImages[speakingStyle]) {
      const emotionName = speakingStyle;
      const images = emotionImages[emotionName];
      
      if (images && images.length > 0) {
        // Cycle through images instead of random selection
        setCurrentImageIndices(prev => {
          const currentIndex = prev[emotionName] || 0;
          const nextIndex = (currentIndex + 1) % images.length;
          
          // Only log in development
          if (process.env.NODE_ENV === 'development') {
            console.log('[EMOTION_DEBUG] 🔄 Cycling image index:', currentIndex, '->', nextIndex, 'for speaking style:', emotionName, 'emotion processing counter:', emotionProcessingCounter, `(${images.length} total images available)`);
          }
          
          const newIndices = {
            ...prev,
            [emotionName]: nextIndex
          };
          return newIndices;
        });
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('[EMOTION_DEBUG] ⚠️ No images available for emotion:', emotionName);
        }
      }
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.log('[EMOTION_DEBUG] ⚠️ Emotion images not loaded or speaking style not found:', { 
          hasEmotionImages: !!emotionImages, 
          speakingStyle, 
          availableEmotions: emotionImages ? Object.keys(emotionImages) : 'not loaded'
        });
      }
    }
  }, [emotionProcessingCounter, speakingStyle]);

  // Initialize image indices when emotion images are first loaded (without cycling)
  useEffect(() => {
    if (emotionImages && Object.keys(emotionImages).length > 0) {
      // Only initialize if indices haven't been set yet
      setCurrentImageIndices(prev => {
        const hasAnyIndices = Object.keys(prev).length > 0;
        if (!hasAnyIndices) {
          const initialIndices = {};
          Object.keys(emotionImages).forEach(emotion => {
            initialIndices[emotion] = 0;
          });
          console.log('[EMOTION_DEBUG] 🎭 Initializing image indices for all emotions:', initialIndices);
          return initialIndices;
        }
        return prev;
      });
    }
  }, [emotionImages]);

  // Get image path for current speaking style - memoized to prevent excessive re-renders
  const getImagePath = useCallback((speakingStyle) => {
    // Only log in development and reduce frequency
    if (process.env.NODE_ENV === 'development' && emotionProcessingCounter % 10 === 0) {
      console.log('[EMOTION_DEBUG] 🖼️ Getting image path for speaking style:', speakingStyle, {
        emotionImages: emotionImages,
        currentImageIndices: currentImageIndices,
        emotionProcessingCounter: emotionProcessingCounter
      });
    }
    
    const emotionName = speakingStyle;
    
    if (emotionImages[emotionName] && emotionImages[emotionName].length > 0) {
      const images = emotionImages[emotionName];
      
      // Use the stored random index from currentImageIndices state
      const storedIndex = currentImageIndices[emotionName];
      
      console.log('[EMOTION_DEBUG] 📊 Image selection details:', {
        emotionName,
        totalImages: images.length,
        storedIndex,
        isValidIndex: storedIndex !== undefined && storedIndex < images.length
      });
      
      if (storedIndex !== undefined && storedIndex < images.length) {
        // Construct full URL using the backend host + relative URL
        const relativeUrl = images[storedIndex].data;
        const fullUrl = `${process.env.REACT_APP_SIM_BACKEND_HOST}${relativeUrl}`;
        
        console.log('[EMOTION_DEBUG] ✅ Selected image URL:', fullUrl);
        return fullUrl;
      } else {
        console.log('[EMOTION_DEBUG] ❌ Invalid stored index:', storedIndex, 'for', images.length, 'images');
      }
    } else {
      console.log('[EMOTION_DEBUG] ⚠️ No images for emotion:', emotionName, 'trying Neutral fallback');
      const images = emotionImages['Neutral'];
      const storedIndex = currentImageIndices['Neutral'];
      if (storedIndex !== undefined && storedIndex < images.length) {
        const relativeUrl = images[storedIndex].data;
        const fullUrl = `${process.env.REACT_APP_SIM_BACKEND_HOST}${relativeUrl}`;
        console.log('[EMOTION_DEBUG] ✅ Using Neutral fallback image:', fullUrl);
        return fullUrl;
      }
    }
    
    console.log('[EMOTION_DEBUG] ❌ No valid image found, returning null');
    return null;
  }, [emotionImages, currentImageIndices, emotionProcessingCounter]);

  const currentImagePath = useMemo(() => getImagePath(speakingStyle), [speakingStyle, getImagePath]);

  // Preload next image and update only after it decodes to avoid blocking TTS start
  useEffect(() => {
    if (!currentImagePath) {
      console.log('[IMAGE_DEBUG] No currentImagePath available for preloading');
      return;
    }
    console.log('[IMAGE_DEBUG] Preloading image:', currentImagePath);
    const img = new Image();
    img.src = currentImagePath;
    const apply = () => {
      console.log('[IMAGE_DEBUG] Image preloaded successfully, setting displayedImagePath:', currentImagePath);
      setDisplayedImagePath(currentImagePath);
    };
    if (img.decode) {
      img.decode().then(apply).catch((error) => {
        console.error('[IMAGE_DEBUG] Image decode failed:', error);
        apply(); // Still set the path even if decode fails
      });
    } else {
      img.onload = apply;
      img.onerror = (error) => {
        console.error('[IMAGE_DEBUG] Image load failed:', error);
        apply(); // Still set the path even if load fails
      };
    }
  }, [currentImagePath]);

  // Fullscreen toggle function
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => {
      const newFullscreen = !prev;
      console.log('[FULLSCREEN_DEBUG] Toggling fullscreen:', prev, '->', newFullscreen);
      if (newFullscreen) {
        console.log('[FULLSCREEN_DEBUG] Entering fullscreen mode with:', {
          currentImagePath,
          displayedImagePath,
          speakingStyle,
          isLoadingImages
        });
      }
      return newFullscreen;
    });
  }, [currentImagePath, displayedImagePath, speakingStyle, isLoadingImages]);

  // Handle media stream changes from ChatInput
  const handleMediaStreamChange = useCallback((stream) => {
    setMediaStream(stream);
    if (stream) {
      setPersistentMediaStream(stream);
    }
  }, []);

  // Handle recording state changes from ChatInput
  const handleRecordingStateChange = useCallback((recording) => {
    setIsRecording(recording);
  }, []);

  // Recording methods are now accessed directly via chatInputRef to prevent infinite loops

  // Handle recording stopped from ChatInput
  const handleRecordingStopped = useCallback(() => {
    // Do not set processing here. Let ChatInput drive processing state.
  }, []);

  // Handle processing state changes from ChatInput
  const handleProcessingChange = useCallback((processing) => {
    setIsProcessing(processing);
  }, []);

  // Memoize the onComplete callback to prevent infinite re-renders
  const handleChatInputComplete = useCallback((user_message, bot_message) => {
    // Stop timer and get response time
    const responseTime = stopTimer();
    appendMessage("U", user_message, responseTime);
    console.log("bot_message", bot_message);
    // console.log(
    //   "chat input is complete...now show the output with message..",
    //   bot_message
    // );
    setNextStage(CHAT_OUTPUT);
    console.log("bot_message", bot_message.bot_response);
    setBotMessage(bot_message.bot_response);
    
    // Validate emotion and use fallback if needed
    const validatedEmotion = validateEmotion(bot_message.speaking_style);
    console.log('[EMOTION_DEBUG] 🎭 Processing emotion:', {
      original: bot_message.speaking_style,
      validated: validatedEmotion,
      currentSpeakingStyle: speakingStyle,
      emotionProcessingCounter: emotionProcessingCounter
    });
    
    setSpeakingStyle(validatedEmotion);
    
    // Increment emotion processing counter to trigger image change even for same speaking style
    setEmotionProcessingCounter(prev => {
      const newCounter = prev + 1;
      console.log('[EMOTION_DEBUG] 🔢 Incrementing emotion processing counter:', prev, '->', newCounter);
      return newCounter;
    });
    
    // Keep the old counter for backward compatibility (if needed elsewhere)
    setSpeakingStyleCounter(prev => prev + 1);
  }, [stopTimer, appendMessage, validateEmotion, speakingStyle, emotionProcessingCounter]);

  // Memoize the setAudioBlob callback
  const handleSetAudioBlob = useCallback((blob) => {
    setAudioBlob(blob);
  }, []);

  // Set up waveform when persistent media stream changes
  useEffect(() => {
    if (persistentMediaStream) {
      // Always set up the regular waveform ref first
      if (waveformRef.current) {
        waveformRef.current.setupFromMediaStream(persistentMediaStream);
      }
      // Also set up fullscreen waveform ref if it exists
      if (fullscreenWaveformRef.current) {
        fullscreenWaveformRef.current.setupFromMediaStream(persistentMediaStream);
      }
    } else {
      // Teardown both waveform refs when no stream
      if (waveformRef.current) {
        waveformRef.current.teardown();
      }
      if (fullscreenWaveformRef.current) {
        fullscreenWaveformRef.current.teardown();
      }
    }
  }, [persistentMediaStream]);

  // Set up fullscreen waveform when entering fullscreen mode
  useEffect(() => {
    if (isFullscreen && persistentMediaStream && fullscreenWaveformRef.current) {
      console.log('Setting up fullscreen waveform with media stream:', persistentMediaStream);
      // Set up the fullscreen waveform with the current media stream
      fullscreenWaveformRef.current.setupFromMediaStream(persistentMediaStream);
    }
  }, [isFullscreen, persistentMediaStream]);

  // Clear media streams when exiting fullscreen - REMOVED to prevent infinite loop
  // Media streams will be cleaned up naturally when components unmount

  // Clear processing state when returning to input stage
  useEffect(() => {
    if (nextStage === CHAT_INPUT) {
      setIsProcessing(false);
    }
  }, [nextStage]);


  // Handle body scroll lock when fullscreen is active
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isFullscreen]);

  // Handle keyboard shortcuts for fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      // F key to toggle fullscreen
      // if (e.code === 'KeyF') {
      //   e.preventDefault();
      //   e.stopPropagation();
      //   toggleFullscreen();
      // }
      // ESC key to exit fullscreen
      if (e.code === 'Escape' && isFullscreen) {
        e.preventDefault();
        e.stopPropagation();
        setIsFullscreen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreen]);

  useEffect(() => {
    const setup = async () => {
      setPersona({
        ...DEFAULT_PERSONAS.find((p) => p.id === personaId),
        id: personaId,
      });
      // console.log("persona and chat session initiated successfully!");

      if (!azureToken) {
        const tokenObj = await azureRecorderSetup();
        // console.log("azure token is set!", tokenObj);
        setAzureToken(tokenObj);
      }
    };

    setup();
  }, [azureToken]);

  useEffect(() => {
    console.log("Setting up Azure token refresh interval...");
    const interval = setInterval(async () => {
      console.log("Refreshing Azure token...");
      try {
        const tokenObj = await azureRecorderSetup();
        setAzureToken(tokenObj);
      } catch (error) {
        console.error("Failed to refresh Azure token:", error);
        setError(true);
      }
    }, 60000); // Refresh every 1 minute

    return () => {
      console.log("Clearing Azure token refresh interval...");
      clearInterval(interval);
    };
  }, []);

  const azureRecorderSetup = async () => {
    let tokenObj = await getTokenOrRefresh();
    console.log("Token object:", tokenObj);
    if (tokenObj.error) {
      setError(true);
      throw new Error("Failed in setting up this chat!");
    }
    return tokenObj;
  };


  function chatOutputComplete(bot_message) {
    if (messageLimitReached) {
      return;
    }

    // Clear processing state
    setIsProcessing(false);

    // Reset timer for new interaction
    resetTimer();

    appendMessage("B", bot_message);
    refreshCheckList();
    const url = `${process.env.REACT_APP_SIM_BACKEND_HOST}/sim/${simulation.simulationId}/${chat.chatId}`;
    fetchAuthenticatedUrl(url).then((resp) => {
      console.log("resp", resp);
      setStage(resp.data.chat_response[0].stage);
    })
    // console.log("chat output is now complete..", bot_message);
    setNextStage(CHAT_INPUT);
    setBotMessage("");

    // Start timer when AI finishes speaking
    startTimer();
  }


  //This method helps in scrolling to the bottom of the div
  const setHistoryContainerRef = (element) => {
    if (element) {
      historyContainerRef.current = element;
      historyContainerRef.current.scrollTop =
        historyContainerRef.current.scrollHeight;
    }
  };
  const handleEndChat = async (shouldRedirect = true) => {
    console.log("ending chat", simulation.simulationId, chat.chatId);
    let response;
    if (simulation.summaryRequired) {
      response = await endChat(simulation.simulationId, chat.chatId, summary);


    } else {

      response = await endChat(simulation.simulationId, chat.chatId, summary);

    }
    if (response.data) {
      // Show the feedback dialog instead of immediately redirecting
      localStorage.setItem("session_end_time", Date.now());
      simulationFeedbackDialog.openDialog({});
    }
    return;
  }

  // useEffect(() => {
  //   // This effect handles the unmounting and remounting of ChatInput
  //   // COMMENTED OUT - This was causing infinite re-render loops
  //   if (nextStage === CHAT_INPUT) {
  //     let mountTimer;

  //     const toggleMount = () => {
  //       setIsChatInputMounted(false);

  //       // Remount after a short delay
  //       mountTimer = setTimeout(() => {
  //         setIsChatInputMounted(true);
  //       }, 100); // 1 second delay before remounting
  //     };

  //     // Initial unmount/remount cycle
  //     const cycleTimer = setInterval(() => {
  //       toggleMount();
  //     }, 8000 * 60); // Cycle every 8 seconds

  //     return () => {
  //       console.log("clearing cycle timer");
  //       clearInterval(cycleTimer);
  //       clearTimeout(mountTimer);
  //     };
  //   }
  // }, [nextStage]);

  return (
    <div className="container min-h-[75vh] max-h-[80vh] overflow-hidden overflow-x-hidden flex flex-col">
      {simulation && azureToken.authToken && (
        <>
          <div className="grid grid-cols-1 gap-4">
            {/* <div>
                <Persona person={persona} />
            </div> */}
            <div>
              <ChatStatus simulation={simulation} chat={chat} personaImage={personaImage} onClose={handleEndChat} />
            </div>
          </div>
          <div
            // ref={setHistoryContainerRef}
            className="flex-1 overflow-y-auto flex flex-col justify-between items-center"
          >
            <div id="messages" className={`w-full h-fit mb-10  ${simulation.useBotEmotions ? 'mt-5 overflow-visible' : 'overflow-y-auto'}`}>

              {simulation.useBotEmotions ?
                <ChatOutputWithImage 
                  speakingStyle={speakingStyle} 
                  showHalo={showHalo} 
                  simType={simulation.simType} 
                  feedbackMessages={feedbackMessages} 
                  showFeedback={showFeedback}
                  imagePath={displayedImagePath}
                  isLoadingImages={isLoadingImages}
                  onToggleFullscreen={toggleFullscreen}
                /> :
                <ChatHistory messages={messages} personaImage={personaImage} feedbackMessages={feedbackMessages} showFeedback={showFeedback} isTimeRecordingEnabled={simulation.config.isTimeRecordingEnabled || false} />}
              <div></div>
              <div>
                {nextStage === CHAT_OUTPUT && (
                  <ChatOutput
                    stage={stage}
                    simulation={simulation}
                    azureToken={azureToken}
                    message={botMessage}
                    speakingStyle={speakingStyle}
                    onComplete={chatOutputComplete}
                    setAzureToken={setAzureToken}
                    setError={setError}
                    setShowHalo={setShowHalo}
                    setupWaveformFromSpeaker={setupWaveformFromSpeaker}
                  />
                )}
                {/* Waveform Canvas */}
              </div>
            </div>
            <div className="sticky bottom-0 rounded-lg w-fit">
              <div className="max-w-md mx-auto">
                {nextStage === CHAT_INPUT && isChatInputMounted ? (
                  <ChatInput
                    ref={chatInputRef}
                    url={chatInputURL}
                    azureToken={azureToken}
                    onComplete={handleChatInputComplete}
                    setAzureToken={setAzureToken}
                    setError={setError}
                    refreshCheckList={refreshCheckList}
                    setAudioBlob={handleSetAudioBlob}
                    showInputElement={nextStage === CHAT_INPUT}
                    stopTimer={stopTimer}
                    currentResponseTime={currentResponseTime}
                    isDialogOpen={simulationFeedbackDialog.dialogProps.open}
                    imagePath={currentImagePath}
                    speakingStyle={speakingStyle}
                    isFullscreen={isFullscreen}
                    onToggleFullscreen={toggleFullscreen}
                    onMediaStreamChange={handleMediaStreamChange}
                    onRecordingStateChange={handleRecordingStateChange}
                    onRecordingStopped={handleRecordingStopped}
                    onRecognizerReady={setIsRecognizerReady}
                    onProcessingChange={handleProcessingChange}
                  />
                ):(
                  WaveformComponent
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {!location.state && !azureToken.authToken && (
        <div>Unable to start this chat! Redirecting in 5 seconds...</div>
      )}

      {
        error && (
          <Dialog open={true} onClose={() => window.location.reload()}>
            <div className="p-4">
              <p>Refresh this page to continue from where you left off.</p>
              <button
                className="mt-2 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                onClick={() => window.location.reload()}
              >
                Refresh
              </button>
            </div>
          </Dialog>
        )

      }
      {messageLimitReached && (
        <Dialog open={true} onClose={() => simulationFeedbackDialog.openDialog({})}>
          <div className="p-4">
            <p>This role play has now ended. Thank you for engaging with this simulation.</p>
            <button
              className="mt-2 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
              onClick={() => simulationFeedbackDialog.openDialog({})}
            >
              Provide Feedback
            </button>
          </div>

        </Dialog>
      )}

      {/* SimulationFeedback Dialog */}
      <simulationFeedbackDialog.Dialog
        {...simulationFeedbackDialog.dialogProps}
      />

      {/* Fullscreen Overlay - moved to parent component to persist across stages */}
      {isFullscreen && (
        <div className="fullscreen-overlay">
          {error && (
            <div className="z-50">
              <div id="error-message" className="text-red-500 text-sm font">
                Refresh this page to continue from where you left off
                <button
                  className="text-red-500 hover:text-red-700 focus:outline-none"
                  onClick={() => window.location.reload()}
                >
                  <svg width="20" height="20" viewBox="0 0 40 40">
                    <path d="M 10,10 L 20,20 M 20,10 L 10,20" stroke="black" strokeWidth="2" />
                  </svg>
                </button>
              </div>
            </div>
          )}
          {/* Exit Cross Icon - positioned outside centered content */}
          <button
            className="fullscreen-exit-button"
            onClick={() => setIsFullscreen(false)}
            aria-label="Exit fullscreen"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>

          {/* Centered Content Container */}
          <div className="fullscreen-content">
            {/* Emotion Image */}
          {displayedImagePath ? (
            <div className="fullscreen-image-container">
              <img
                src={displayedImagePath}
                alt={`Speaking style: ${speakingStyle}`}
                className={`fullscreen-image `}
                onError={(e) => {
                  console.error(`[FULLSCREEN_DEBUG] Failed to load image: ${displayedImagePath}`);
                  e.currentTarget.style.display = 'none';
                }}
                onLoad={() => {
                  console.log(`[FULLSCREEN_DEBUG] Successfully loaded image: ${displayedImagePath}`);
                }}
              />
            </div>
          ) : (
            <div className="fullscreen-loading">
              {isLoadingImages ? 'Loading emotion images...' : 'Loading emotion image...'}
            </div>
          )}

          {/* Recording Controls with inline waveform - only show during input stage when not processing */}
          {nextStage === CHAT_INPUT && !isProcessing && (
            <div className="fullscreen-controls">
              {!isRecording ? (
                <>
                  <button
                    className="fullscreen-button fullscreen-button-start"
                    disabled={!isRecognizerReady}
                    onClick={() => {
                      console.log('Fullscreen start recording clicked');
                      if (chatInputRef.current && chatInputRef.current.startRecording) {
                        console.log('Using ChatInput ref to start recording');
                        chatInputRef.current.startRecording();
                      } else {
                        console.error('Recording methods not available in fullscreen mode');
                      }
                    }}
                  >
                    Start Recording
                  </button>
                  {!isRecognizerReady && (
                    <div className="text-xs text-gray-300 mt-2">Initializing microphone... please wait</div>
                  )}
                  <div className="inline-waveform-container">
                    <WaveformCanvas 
                      ref={fullscreenWaveformRef} 
                      {...fullscreenWaveformProps}
                      isBotSpeaking={false}
                    />
                  </div>
                </>
              ) : (
                <>
                  <button
                    className="fullscreen-button fullscreen-button-stop"
                    onClick={() => {
                      console.log('Fullscreen stop recording clicked');
                      if (chatInputRef.current && chatInputRef.current.stopRecording) {
                        console.log('Using ChatInput ref to stop recording');
                        chatInputRef.current.stopRecording();
                      } else {
                        console.error('Recording methods not available in fullscreen mode');
                      }
                    }}
                  >
                    Stop Recording
                  </button>
                  <div className="inline-waveform-container">
                    <WaveformCanvas 
                      ref={fullscreenWaveformRef} 
                      {...fullscreenWaveformProps}
                      isBotSpeaking={false}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {/* Bot audio waveform or Processing Animation */}
          {nextStage === CHAT_OUTPUT ? (
            WaveformComponent
          ) : isProcessing ? (
            <div className="fullscreen-processing">
              <div className="processing-dots">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
              {/* <div className="processing-text">Processing your message...</div> */}
            </div>
          ) : null}

          {/* Instructions */}
          <div className="fullscreen-instructions">
            Press <span>Space</span> to record • <span>ESC</span> to exit fullscreen
          </div>
          </div> {/* Close fullscreen-content div */}
        </div>
      )}
    </div>
  );
}

export default ChatDetails;
